import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import MainNavigation from '../../components/ui/MainNavigation';
import BreadcrumbNavigation from '../../components/ui/BreadcrumbNavigation';
import ServiceCategorySelector from './components/ServiceCategorySelector';
import RequirementSpecification from './components/RequirementSpecification';
import TimelineBudgetForm from './components/TimelineBudgetForm';
import ContactDetailsForm from './components/ContactDetailsForm';
import FileUploadSection from './components/FileUploadSection';
import ReviewSubmitForm from './components/ReviewSubmitForm';
import ProgressIndicator from './components/ProgressIndicator';
import StepNavigation from './components/StepNavigation';
import Icon from '../../components/AppIcon';


const RFQRequestForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [currentStep, setCurrentStep] = useState(0);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [formData, setFormData] = useState({});
  const [files, setFiles] = useState([]);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [user] = useState({
    id: 1,
    name: "John Doe",
    email: "john.doe@company.com",
    role: "customer"
  });

  // Mock service categories
  const serviceCategories = [
    {
      id: 'consultancy',
      name: 'IT Consultancy',
      icon: 'Users',
      description: 'Strategic IT planning, digital transformation, and business analysis services',
      tags: ['Strategy', 'Planning', 'Analysis', 'Transformation', 'Optimization']
    },
    {
      id: 'infrastructure',
      name: 'Infrastructure Solutions',
      icon: 'Server',
      description: 'Server setup, cloud migration, data center solutions, and virtualization',
      tags: ['Servers', 'Cloud', 'Data Center', 'Virtualization', 'Backup']
    },
    {
      id: 'networking',
      name: 'Networking Solutions',
      icon: 'Wifi',
      description: 'LAN/WAN setup, WiFi deployment, network security, and monitoring',
      tags: ['LAN', 'WiFi', 'Security', 'Monitoring', 'Connectivity']
    },
    {
      id: 'software',
      name: 'Software Development',
      icon: 'Code',
      description: 'Custom software, web applications, mobile apps, and system integration',
      tags: ['Custom Development', 'Web Apps', 'Mobile', 'Integration', 'ERP']
    },
    {
      id: 'policy',
      name: 'Policy & Compliance',
      icon: 'Shield',
      description: 'IT governance, data protection, cybersecurity policies, and compliance',
      tags: ['Governance', 'Privacy', 'Security', 'Compliance', 'Risk Management']
    },
    {
      id: 'strategy',
      name: 'Strategic Planning',
      icon: 'Target',
      description: 'Digital strategy, IT roadmaps, technology adoption, and change management',
      tags: ['Digital Strategy', 'Roadmap', 'Innovation', 'Change Management', 'Adoption']
    }
  ];

  // Form steps configuration
  const steps = [
    {
      title: 'Service Category',
      description: 'Choose service type',
      component: 'category'
    },
    {
      title: 'Requirements',
      description: 'Specify details',
      component: 'requirements'
    },
    {
      title: 'Timeline & Budget',
      description: 'Set expectations',
      component: 'timeline'
    },
    {
      title: 'Contact Details',
      description: 'Your information',
      component: 'contact'
    },
    {
      title: 'Documents',
      description: 'Upload files',
      component: 'files'
    },
    {
      title: 'Review & Submit',
      description: 'Final review',
      component: 'review'
    }
  ];

  // Initialize form data from URL params or localStorage
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const categoryId = urlParams?.get('category');
    const serviceId = urlParams?.get('service');
    
    // Load draft from localStorage
    const savedDraft = localStorage.getItem('rfq-draft');
    if (savedDraft) {
      try {
        const draft = JSON.parse(savedDraft);
        setFormData(draft?.formData || {});
        setFiles(draft?.files || []);
        if (draft?.selectedCategory) {
          setSelectedCategory(draft?.selectedCategory);
        }
        if (draft?.currentStep) {
          setCurrentStep(draft?.currentStep);
        }
      } catch (error) {
        console.error('Error loading draft:', error);
      }
    }

    // Pre-select category from URL
    if (categoryId && !selectedCategory) {
      const category = serviceCategories?.find(cat => cat?.id === categoryId);
      if (category) {
        setSelectedCategory(category);
        setCurrentStep(1); // Skip to requirements step
      }
    }
  }, [location?.search, selectedCategory]);

  // Save draft to localStorage
  const saveDraft = () => {
    const draft = {
      formData,
      files,
      selectedCategory,
      currentStep,
      timestamp: new Date()?.toISOString()
    };
    localStorage.setItem('rfq-draft', JSON.stringify(draft));
    
    // Show success message (you could use a toast library here)
    alert('Draft saved successfully!');
  };

  // Validation functions
  const validateStep = (step) => {
    const newErrors = {};

    switch (step) {
      case 0: // Category selection
        if (!selectedCategory) {
          newErrors.category = 'Please select a service category';
        }
        break;

      case 1: // Requirements
        if (!formData?.projectTitle?.trim()) {
          newErrors.projectTitle = 'Project title is required';
        }
        if (!formData?.projectDescription?.trim()) {
          newErrors.projectDescription = 'Project description is required';
        }
        if (!formData?.priorityLevel) {
          newErrors.priorityLevel = 'Priority level is required';
        }
        if (!formData?.projectSize) {
          newErrors.projectSize = 'Project size is required';
        }
        break;

      case 2: // Timeline & Budget
        if (!formData?.preferredStartDate) {
          newErrors.preferredStartDate = 'Preferred start date is required';
        }
        if (!formData?.requiredCompletionDate) {
          newErrors.requiredCompletionDate = 'Required completion date is required';
        }
        if (!formData?.budgetRange) {
          newErrors.budgetRange = 'Budget range is required';
        }
        if (!formData?.budgetType) {
          newErrors.budgetType = 'Budget type is required';
        }
        if (!formData?.timelineFlexibility) {
          newErrors.timelineFlexibility = 'Timeline flexibility is required';
        }
        break;

      case 3: // Contact details
        if (!formData?.contactName?.trim()) {
          newErrors.contactName = 'Contact name is required';
        }
        if (!formData?.contactEmail?.trim()) {
          newErrors.contactEmail = 'Contact email is required';
        } else if (!/\S+@\S+\.\S+/?.test(formData?.contactEmail)) {
          newErrors.contactEmail = 'Please enter a valid email address';
        }
        if (!formData?.contactPhone?.trim()) {
          newErrors.contactPhone = 'Contact phone is required';
        }
        if (!formData?.companyName?.trim()) {
          newErrors.companyName = 'Company name is required';
        }
        if (!formData?.industry) {
          newErrors.industry = 'Industry is required';
        }
        if (!formData?.companySize) {
          newErrors.companySize = 'Company size is required';
        }
        if (!formData?.preferredContact) {
          newErrors.preferredContact = 'Preferred contact method is required';
        }
        if (!formData?.decisionAuthority) {
          newErrors.decisionAuthority = 'Decision making authority is required';
        }
        if (!formData?.termsAccepted) {
          newErrors.termsAccepted = 'You must accept the terms and conditions';
        }
        break;

      case 4: // Files (optional step)
        // No validation required for file upload
        break;

      case 5: // Review
        // Final validation before submission
        if (!selectedCategory) {
          newErrors.general = 'Service category is required';
        }
        if (!formData?.projectTitle || !formData?.projectDescription) {
          newErrors.general = 'Project details are incomplete';
        }
        if (!formData?.contactName || !formData?.contactEmail) {
          newErrors.general = 'Contact information is incomplete';
        }
        break;
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  // Navigation handlers
  const handleNext = () => {
    if (validateStep(currentStep)) {
      if (currentStep === steps?.length - 1) {
        handleSubmit();
      } else {
        setCurrentStep(currentStep + 1);
      }
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleEditStep = (stepIndex) => {
    setCurrentStep(stepIndex);
  };

  // Form submission
  const handleSubmit = async () => {
    if (!validateStep(currentStep)) {
      return;
    }

    setIsSubmitting(true);

    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 2000));

      // Generate RFQ tracking number
      const trackingNumber = `RFQ-${Date.now()?.toString()?.slice(-6)}`;

      // Clear draft from localStorage
      localStorage.removeItem('rfq-draft');

      // Show success and redirect
      alert(`RFQ submitted successfully! Tracking number: ${trackingNumber}`);
      navigate('/rfq-management', { 
        state: { 
          newRFQ: {
            trackingNumber,
            category: selectedCategory?.name,
            title: formData?.projectTitle,
            status: 'submitted',
            submittedAt: new Date()?.toISOString()
          }
        }
      });
    } catch (error) {
      console.error('Submission error:', error);
      alert('Error submitting RFQ. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Render current step component
  const renderStepComponent = () => {
    switch (currentStep) {
      case 0:
        return (
          <ServiceCategorySelector
            selectedCategory={selectedCategory}
            onCategorySelect={setSelectedCategory}
            categories={serviceCategories}
          />
        );
      case 1:
        return (
          <RequirementSpecification
            selectedCategory={selectedCategory}
            formData={formData}
            onFormDataChange={setFormData}
            errors={errors}
          />
        );
      case 2:
        return (
          <TimelineBudgetForm
            formData={formData}
            onFormDataChange={setFormData}
            errors={errors}
          />
        );
      case 3:
        return (
          <ContactDetailsForm
            formData={formData}
            onFormDataChange={setFormData}
            errors={errors}
          />
        );
      case 4:
        return (
          <FileUploadSection
            files={files}
            onFilesChange={setFiles}
            maxFiles={5}
            maxSizePerFile={10}
          />
        );
      case 5:
        return (
          <ReviewSubmitForm
            formData={formData}
            selectedCategory={selectedCategory}
            files={files}
            onSubmit={handleSubmit}
            onEdit={handleEditStep}
            isSubmitting={isSubmitting}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <MainNavigation user={user} rfqCount={3} />
      <div className="pt-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <BreadcrumbNavigation />
          
          {/* Header */}
          <div className="text-center mb-8">
            <div className="flex items-center justify-center mb-4">
              <div className="p-3 bg-primary/10 rounded-lg">
                <Icon name="FileText" size={32} className="text-primary" />
              </div>
            </div>
            <h1 className="text-3xl font-bold text-foreground mb-2">Request for Quotation</h1>
            <p className="text-lg text-muted-foreground">
              Tell us about your ICT needs and get a customized solution proposal
            </p>
          </div>

          {/* Progress Indicator */}
          <ProgressIndicator
            currentStep={currentStep}
            totalSteps={steps?.length}
            steps={steps}
          />

          {/* Error Display */}
          {errors?.general && (
            <div className="mb-6 p-4 bg-error/10 border border-error/20 rounded-lg">
              <div className="flex items-center space-x-2">
                <Icon name="AlertCircle" size={16} className="text-error" />
                <p className="text-sm text-error font-medium">{errors?.general}</p>
              </div>
            </div>
          )}

          {/* Step Content */}
          <div className="bg-card border border-border rounded-lg p-6 mb-8">
            {renderStepComponent()}
          </div>

          {/* Step Navigation */}
          <StepNavigation
            currentStep={currentStep}
            totalSteps={steps?.length}
            onNext={handleNext}
            onPrevious={handlePrevious}
            onSaveDraft={saveDraft}
            isNextDisabled={currentStep === 0 && !selectedCategory}
            isSubmitting={isSubmitting}
            showSaveDraft={currentStep < steps?.length - 1}
          />

          {/* Help Section */}
          <div className="mt-12 bg-muted/50 border border-border rounded-lg p-6">
            <div className="flex items-start space-x-3">
              <Icon name="HelpCircle" size={20} className="text-primary mt-0.5" />
              <div>
                <h3 className="font-semibold text-foreground mb-2">Need Help?</h3>
                <p className="text-sm text-muted-foreground mb-3">
                  Our team is here to assist you with your RFQ. Contact us if you have any questions.
                </p>
                <div className="flex flex-wrap gap-4 text-sm">
                  <div className="flex items-center space-x-2">
                    <Icon name="Phone" size={14} className="text-muted-foreground" />
                    <span className="text-muted-foreground">+256 700 123 456</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Icon name="Mail" size={14} className="text-muted-foreground" />
                    <span className="text-muted-foreground">rfq@techtribes.ug</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Icon name="MessageCircle" size={14} className="text-muted-foreground" />
                    <span className="text-muted-foreground">Live Chat Available</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RFQRequestForm;